import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type {
  InventoryCreateSchema,
  InventoryQueryAllSchema,
  InventoryQuerySchema,
  InventoryUpdateSchema,
} from '@/_pages/settings/inventory-kits/shared/schemas/inventory';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

export function getInventoriesQuery(apiParamStr = `${defaultApiParamStr}&filter[orphan]=true`) {
  return {
    queryKey: ['INVENTORIES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<InventoryQueryAllSchema>({
        endpoint:
          '/api/v1/inventories?include=children.inventory_category,children.logo,inventory_category,vendors,bin_locations,inventory_volume,logo,secondary_skus',
        params: `${apiParamStr}`,
      });
      return res;
    },
  };
}

export function useGetInventories(apiParamStr: string) {
  return useSuspenseQuery(getInventoriesQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['INVENTORIES'] });
};

export const useGetInStockInventoriesMutation: UseControlledMutation<InventoryQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page, ...rest }) => {
      return queryClient.fetchQuery({
        queryKey: ['IN_STOCK_INVENTORIES_ASYNC', { search, per_page, ...rest }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InventoryQueryAllSchema>({
            endpoint: '/api/v1/inventories?filter[parents]=false&filter[in_stock]=true',
            params: { search, per_page, include: 'logo,inventory_category', ...rest },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const useGetInventoriesMutation: UseControlledMutation<InventoryQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page, ...rest }) => {
      return queryClient.fetchQuery({
        queryKey: ['INVENTORIES_ASYNC', { search, per_page, ...rest }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InventoryQueryAllSchema>({
            endpoint: '/api/v1/inventories',
            params: { search, per_page, include: 'logo,inventory_category', ...rest },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const useCreateMutation = () =>
  useMutation<{ data: InventoryQuerySchema }, ApiError, InventoryCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/inventories/',
        payload,
      }),
  });

export function getInventoryQuery(id: string) {
  return {
    queryKey: ['INVENTORY', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: InventoryQuerySchema }>({
        endpoint: `/api/v1/inventories/${id}`,
        params: {
          include:
            'children.inventory_category,children.logo,inventory_category,vendors,vendor_details.vendor,bin_locations,inventory_volume,images,logo,secondary_skus,brand.icon,parent',
        },
      });

      return res;
    },
  };
}

export const invalidateInventoryQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['INVENTORY', id] });
};

export function useGetInventory(id: string) {
  return useSuspenseQuery(getInventoryQuery(id));
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: InventoryQuerySchema }, unknown, Partial<InventoryUpdateSchema>>({
    mutationKey: ['INVENTORY_UPDATE', id],
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/inventories/${id}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'inventories', id] });
    },
  });

export type UseInventoryUpdateMutation = ReturnType<typeof useUpdateMutation>;

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['INVENTORY_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/inventories/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });

export const useGetLabelFourSixGenerationMutation = (id: string) =>
  useMutation<
    {
      data: {
        name: string;
        pdf_url: string;
      };
    },
    ApiError
  >({
    mutationFn: () =>
      apiClient.request({
        endpoint: `/api/v1/inventory-labels/${id}`,
        method: 'GET',
        init: { headers: { 'Content-Type': 'application/pdf', 'response-type': 'blob' } },
      }),
  });

export const useGetSelfLabelGenerationMutation = (id: string) =>
  useMutation<
    {
      data: {
        name: string;
        pdf_url: string;
      };
    },
    ApiError
  >({
    mutationFn: () =>
      apiClient.request({
        endpoint: `/api/v1/inventory-shelf-tag-labels/${id}`,
        method: 'GET',
        init: { headers: { 'Content-Type': 'application/pdf', 'response-type': 'blob' } },
      }),
  });

export const useBulkUpdateInventoryMutation = () =>
  useMutation<unknown, unknown, { items: Partial<InventoryUpdateSchema>[] }>({
    mutationKey: ['INVENTORY_BULK_UPDATE'],
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/inventories/bulk-update',
        method: 'POST',
        payload,
      }),
  });
